import {useContext, useEffect} from 'react';
import {useLocation, useParams} from 'react-router-dom';
import Select from 'react-select';
import {Broker} from "../../types/Broker";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {SelectUtility} from "../common/SelectUtility";
import {UrlUtility} from "../common/UrlUtility";
import {PhoneUtility} from '../common/PhoneUtility';

interface BrokersListPageURLQueryParams {
    sortDirection?: string;
}

export const BrokersListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.brokers.pathSegment}`;
    const dataStore = useRenderableDataStore({
        brokersList: [],
        loading: true,
        sortDirection: Constants.sortDirections.asc,

        prevUrlQueryString: null,
        prevParams: null,
    })
    const location = useLocation();
    const routeParams = useParams();

    // Update state if URL changes.
    if (
        (location && location.search) !== dataStore.data().prevUrlQueryString ||
        routeParams !== dataStore.data().prevParams
    ) {
        let sortDirection = Constants.sortDirections.asc;

        let queryParams: BrokersListPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);
        if (queryParams.sortDirection) {
            sortDirection = queryParams.sortDirection;
        }

        dataStore.updateAndRender({
            sortDirection: sortDirection,
            prevUrlQueryString: location.search,
            prevParams: routeParams,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        ['' + dataStore.data().sortDirection]
    );

    function reloadList() {
        dataStore.updateAndRender({loading: true})
        ApiUtility.getBrokersList(dataStore.data().sortDirection)
            .then(function (listData) {
                dataStore.updateAndRender({
                    brokersList: listData.list,
                    loading: false
                });
            })
            .catch(function (error) {
                dataStore.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the broker list.`);
            });
    }

    function handleSortChange(option: SelectOption) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {
            ...currentQueryParams,
            sortDirection: option.value,
        };
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function goToAddBrokerPage() {
        context.navigator.navigateTo(`${urlBase}/add${location.search}`);
    }

    function goToUpdateBrokerPage(broker: Broker) {
        context.navigator.navigateTo(`${urlBase}/${broker.id}`);
    }

    function renderBrokersListDisplay(brokersList: Broker[]) {
        let brokersListDisplay = [];

        for (let i = 0; i < brokersList.length; i++) {
            const broker = brokersList[i];
            const isInactive = broker.active === false;
            const inactiveSuffix = isInactive ? ' (Inactive)' : '';

            brokersListDisplay.push(
                <div
                    key={i + 1}
                    className={`broker-list-item${isInactive ? ' broker-list-item-inactive' : ''}`}
                    onClick={() => goToUpdateBrokerPage(broker)}
                >
                    <div className="broker-list-item-ticket-circle"></div>
                    <div className="broker-list-item-content">
                        <div className="broker-list-item-edit-section">
                            <img
                                className="broker-list-item-edit-icon"
                                src="/static/images/edit-icon-green.svg"
                            />
                        </div>
                        <div className="broker-name-section">
                            {broker.name}{inactiveSuffix}
                        </div>
                        <div className="broker-general-info-section">
                            <div className="broker-general-info-content">
                                <div className="broker-general-info-left">
                                    <div className="broker-general-info-left-content">
                                        <div className="broker-contact-name">
                                            {broker.external_contact.name}
                                        </div>
                                        <div className="broker-contact-phone">
                                            {PhoneUtility.dbPhoneToUsersListDisplayPhone('' + broker.external_contact.phone)}
                                        </div>
                                        <a
                                            href={`mailto:${broker.external_contact.email}`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="broker-contact-email"
                                        >
                                            {broker.external_contact.email}
                                        </a>
                                    </div>
                                </div>
                                <div className="broker-general-info-right">
                                    <div className="broker-general-info-right-content">
                                        <div className="broker-fleet-count">
                                            <div className="broker-fleet-count-content">
                                                <div className="broker-fleet-count-value">
                                                    {broker.unlimited_trucks
                                                        ? <img className="broker-fleet-count-unlimited-icon" src="/static/images/infinity-textcolor.svg" alt="Unlimited"/>
                                                        : broker.fleet_size}
                                                </div>
                                                <img
                                                    className="broker-fleet-count-icon"
                                                    src="/static/images/fleet-size-truck-textcolor.svg"
                                                />
                                            </div>
                                        </div>
                                        <div className="broker-fleet-count-label">
                                            MAX FLEET
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return brokersListDisplay;
    }

    if (dataStore.data().brokersList) {
        return (
            <div className="common-list-page brokers-list-page">
                <div className="common-list-page-header">
                    <div className="common-list-page-header-content">
                        <div className="common-list-page-header-content-left">
                            <div className="common-list-sort-control-container">
                                <Select
                                    className="common-list-sort-control"
                                    classNamePrefix="common-list-sort-control"
                                    options={Constants.listSortOptions}
                                    value={SelectUtility.selectOptionForId(
                                        dataStore.data().sortDirection,
                                        Constants.listSortOptions
                                    )}
                                    onChange={(option) => handleSortChange(option as SelectOption)}
                                    components={{
                                        Control: (controlProps: any) => (
                                            <div
                                                {...(controlProps.innerProps || {})}
                                                className="sort-filter-control"
                                            >
                                                <img
                                                    className="common-list-page-filter-icon sort-filter-icon"
                                                    src={
                                                        dataStore.data().sortDirection === Constants.sortDirections.asc
                                                            ? "/static/images/sort-ascending-textcolor.svg"
                                                            : "/static/images/sort-descending-textcolor.svg"
                                                    }
                                                />
                                                <img
                                                    className="common-list-page-filter-caret sort-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                                <div className="sort-filter-control-hidden-children">
                                                    {controlProps.children}
                                                </div>
                                            </div>
                                        )
                                    }}
                                />
                            </div>
                        </div>
                        <div className="common-list-page-header-content-right">
                            <div
                                className="icon-button add-button"
                                onClick={goToAddBrokerPage}
                            >
                                <div className="icon-button-content">
                                    <img
                                        className="icon-button-image"
                                        src="/static/images/plus-green.svg"
                                    />
                                    <div className="icon-button-text">Add Broker</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={dataStore.data().loading}
                        renderer={() => <OverlayLoadingIndicator />}
                    />
                    {(() => {
                        if (dataStore.data().brokersList.length > 0) {
                            return (
                                <div className="brokers-list-content">
                                    {renderBrokersListDisplay(dataStore.data().brokersList)}
                                </div>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No brokers found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator />
        );
    }
};
